/**
 * Append query params to url string. Supports both fully qualified URLs and partial paths.
 * @param url URL or partial path
 * @param queryParams Query params
 * @returns New URL string (preserving the original form — full or partial)
 *
 * @example
 * ```ts
 *  appendQueryObjectToURL("https://lorem.ipsum", {"foo": "bar"}); // => https://lorem.ipsum/?foo=bar
 *  appendQueryObjectToURL("https://lorem.ipsum/?dolar=sit", {"foo": "bar"}); // => https://lorem.ipsum/?dolar=sit&foo=bar
 *  appendQueryObjectToURL("/file/asset/1/preview", {"foo": "bar"}); // => /file/asset/1/preview?foo=bar
 *  appendQueryObjectToURL("/file/asset/1/preview?dolar=sit", {"foo": "bar"}); // => /file/asset/1/preview?dolar=sit&foo=bar
 *  appendQueryObjectToURL("./file/preview", {"foo": "bar"}); // => ./file/preview?foo=bar
 *  appendQueryObjectToURL("file/preview", {"foo": "bar"}); // => file/preview?foo=bar
 * ```
 */
export const appendQueryObjectToURL = (
	url: string,
	queryParams?: Record<string, number | string | null | undefined> | null,
) => {
	if (queryParams == null || !url) return url;
	const entries = Object.entries(queryParams);
	if (entries.length === 0) return url;

	const DUMMY_BASE = 'https://placeholder.local';
	let isRelative = false;
	let _url: URL;

	try {
		_url = new URL(url);
	} catch {
		isRelative = true;
		_url = new URL(url, DUMMY_BASE);
	}

	entries.forEach(
		([key, value]) =>
			value != null && _url.searchParams.append(key, value.toString()),
	);

	if (!isRelative) return _url.toString();

	// Dot-relative paths: `new URL('./foo', base)` resolves to `/foo`, so we
	// re-attach the leading `.` to keep the original form.
	if (url.startsWith('./')) {
		return '.' + _url.pathname + _url.search + _url.hash;
	}

	// Bare relative paths (no leading `/` and no protocol): `new URL('foo', base)`
	// resolves to `/foo`, so strip the leading `/` added by the parser.
	if (!url.startsWith('/')) {
		return _url.pathname.slice(1) + _url.search + _url.hash;
	}

	return _url.pathname + _url.search + _url.hash;
};

export default appendQueryObjectToURL;
